import { CATEGORY_LABELS, CLIENT_TYPE_LABELS, MARKET_LABELS, PROFILE_LABELS } from '../data/knowledgeBase';
import type { PreparationResult } from '../types';
import { ValueSellingSineWaveChart } from './ValueSellingSineWaveChart';

interface PreparationCardsProps {
  result: PreparationResult;
}

const renderList = (items: string[], emptyFallback: string) => {
  if (!items.length) {
    return <p className="card__empty">{emptyFallback}</p>;
  }

  return (
    <ul className="card__list">
      {items.map((item, index) => (
        <li key={`${item.slice(0, 28)}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

export const PreparationCards = ({ result }: PreparationCardsProps) => {
  const generatedAt = new Date(result.metadata.generatedAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <section className="results" aria-live="polite">
      <div className="results__heading animate-in">
        <h2>{result.tituloFicha}</h2>
        <p>Cenário analisado: {result.cenarioOriginal}</p>
        <div className="results__meta">
          <span>{generatedAt}</span>
          <span>{PROFILE_LABELS[result.contexto.perfil]}</span>
          <span>{CATEGORY_LABELS[result.contexto.categoria]}</span>
          <span>{MARKET_LABELS[result.contexto.mercado]}</span>
          <span>{CLIENT_TYPE_LABELS[result.contexto.tipoCliente]}</span>
        </div>
      </div>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>Orientação comportamental</h3>
          <p className="card__kicker">Linguagem, postura, ritmo e fechamento</p>
        </div>
        <div className="card__grid card__grid--thirds">
          <div className="card__section">
            <h4>Linguagem recomendada</h4>
            {renderList(result.abordagemPerfil.linguagem, 'Sem orientação de linguagem.')}
          </div>
          <div className="card__section">
            <h4>Postura e ritmo</h4>
            {renderList([...result.abordagemPerfil.postura, ...result.abordagemPerfil.ritmo], 'Sem orientação de postura.')}
          </div>
          <div className="card__section">
            <h4>Forma ideal de fechamento</h4>
            {renderList(result.abordagemPerfil.fechamento, 'Sem orientação de fechamento.')}
          </div>
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>OPC</h3>
          <p className="card__kicker">Objetivo, processo e compromisso</p>
        </div>
        <div className="card__section">
          <h4>Objetivo</h4>
          {renderList(result.opc.objetivo, 'Sem objetivos sugeridos.')}
        </div>
        <div className="card__section">
          <h4>Processo</h4>
          {renderList(result.opc.processo, 'Sem processo sugerido.')}
        </div>
        <div className="card__section">
          <h4>Compromisso</h4>
          {renderList(result.opc.compromisso, 'Sem compromisso sugerido.')}
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>SPIN Selling</h3>
          <p className="card__kicker">Perguntas abertas e naturais</p>
        </div>
        <div className="card__grid">
          <div className="card__section">
            <h4>Situação</h4>
            {renderList(result.spin.situacao, 'Sem perguntas de situação.')}
          </div>
          <div className="card__section">
            <h4>Problema</h4>
            {renderList(result.spin.problema, 'Sem perguntas de problema.')}
          </div>
          <div className="card__section">
            <h4>Implicação</h4>
            {renderList(result.spin.implicacao, 'Sem perguntas de implicação.')}
          </div>
          <div className="card__section">
            <h4>Necessidade de solução</h4>
            {renderList(result.spin.necessidadeSolucao, 'Sem perguntas de necessidade de solução.')}
          </div>
        </div>
      </article>

      <article className="card animate-in">
        <div className="card__title-row">
          <h3>EPA</h3>
          <p className="card__kicker">Educar, personalizar e assumir o controle</p>
        </div>
        <div className="card__grid card__grid--thirds">
          <div className="card__section">
            <h4>Educar</h4>
            {renderList(result.epa.educar, 'Sem orientações de educação.')}
          </div>
          <div className="card__section">
            <h4>Personalizar</h4>
            {renderList(result.epa.personalizar, 'Sem orientações de personalização.')}
          </div>
          <div className="card__section">
            <h4>Assumir o controle</h4>
            {renderList(result.epa.assumirControle, 'Sem orientações de controle.')}
          </div>
        </div>
      </article>

      <ValueSellingSineWaveChart map={result.valueSineMap} />
    </section>
  );
};
